import {
  type AsyncPanelProps,
  bindErrorSentence,
  createAsyncPanel,
  ErrorCard,
  Spinner,
} from '@croissants/ui';
import type { JSX, ReactNode } from 'react';
import { guestCatalog } from '../i18n/index';
import { useLocale } from '../state/locale';

/**
 * The two things every screen needs and no screen should re-invent: a busy state
 * that is announced, and a failure that is rendered in the guest's language.
 *
 * Both take their copy from a catalogue rather than a literal (PRD NFR-006), and
 * both are announced to assistive technology (PRD NFR-005) - a spinner that only
 * spins tells a screen-reader user nothing.
 */

export function Loading({ label }: { label?: string }): JSX.Element {
  const { t } = useLocale();
  return (
    <div className="gw-loading" role="status">
      <Spinner size={28} />
      <span>{label ?? t('app.loading')}</span>
    </div>
  );
}

/**
 * ONE binding of the platform's one error renderer, for this app
 * (`bindErrorSentence`). What belongs here is only which catalogue supplies
 * the words, and it belongs here once: the checkout's quote-failure line once
 * spelled the three arguments out itself, which is how a portal ends up with
 * two sentences for one failure.
 */
export const errorText = bindErrorSentence(guestCatalog);

export function ErrorNotice({
  error,
  onRetry,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string;
}): JSX.Element {
  const { locale, t, pt } = useLocale();
  /*
   * One renderer, shared with the other three portals (`errorSentence`). This
   * screen used to return the server's key unless it was exactly
   * `error.unexpected` - so `error.http`, the bare framework refusal that says
   * nothing the status has not, reached a guest as "That request could not be
   * completed." where every other portal named the record.
   */
  return (
    <ErrorCard
      className="gw-notice gw-notice--danger"
      error={error}
      title={title ?? t('error.title')}
      message={errorText(error, locale)}
      requestIdLabel={(id) => t('error.requestId', { id })}
      retryLabel={pt('action.retry')}
      {...(onRetry === undefined ? {} : { onRetry })}
    />
  );
}

/**
 * The guest's panel: the shared props, except that `emptyWhen` comes only with
 * the screen's own `empty` node. The guest has no generic empty sentence - each
 * empty answer means something different to a guest (no bakery serves this
 * address, nothing is on the counter yet) - so a panel asked "is this empty?"
 * with nothing to draw would leave a blank area where the answer was, which is
 * the outcome the panel exists to prevent. Refused here, at compile time - an
 * omitted `empty` and one that is `null`, `undefined` or a boolean alike, since
 * React draws nothing for any of them.
 */
type GuestAsyncPanel = <T>(
  props: AsyncPanelProps<T> &
    (
      | { readonly emptyWhen?: undefined }
      | { readonly emptyWhen: (data: T) => boolean; readonly empty: DrawnNode }
    ),
) => JSX.Element;

/** A node React actually draws: `ReactNode` without the members that render nothing. */
type DrawnNode = Exclude<ReactNode, null | undefined | boolean>;

/**
 * The guest's cards, bound once to the platform's one panel (`createAsyncPanel`,
 * packages/ui/src/async-panel.tsx). The empty card is the screen's own node,
 * drawn as given (`GuestAsyncPanel` above makes a screen pass one); the order
 * page separates a refused code from an unreachable server and the stay page
 * keeps its link back to the list, through the panel's `error` override.
 */
export const AsyncPanel: GuestAsyncPanel = createAsyncPanel({
  pending: <Loading />,
  empty: (custom) => custom ?? null,
  error: (failure, retry) => <ErrorNotice error={failure} onRetry={retry} />,
});
